import type { LexData } from "./types";
import { demoDelegations, realParticipationPoints } from "./governanceDemo";
import { applyDelegationCaps, calculateMembershipParticipationBonus, delegationVoteCap, votingWeightForMembership, type DelegationRecord } from "./voting";

const PERIOD = "2026-H1";
const JURISDICTION_THRESHOLD = 0.4;

export type ConcentrationLevel = "balanced" | "watch" | "breach";

export interface WeightShare {
  id: string;
  label: string;
  detail: string;
  ownWeight: number;
  delegatedWeight: number;
  controlledWeight: number;
  share: number;
  level: ConcentrationLevel;
}

export interface BalanceAlert {
  severity: "watch" | "breach";
  dimension: "firm" | "jurisdiction" | "affiliation";
  label: string;
  message: string;
}

export interface BalanceOverview {
  activeFullMemberCount: number;
  totalWeight: number;
  firmCapShare: number;
  jurisdictionThreshold: number;
  delegationCap: number;
  herfindahlIndex: number;
  concentrationLabel: string;
  byFirm: WeightShare[];
  byJurisdiction: WeightShare[];
  byAffiliation: WeightShare[];
  alerts: BalanceAlert[];
}

/**
 * The single-firm voting-weight cap shrinks as the network grows, so no member
 * can dominate a larger network. Mirrors the sliding holding cap in domain.ts.
 */
export function firmConcentrationCap(activeFullMemberCount: number) {
  if (activeFullMemberCount >= 40) return 0.1;
  if (activeFullMemberCount >= 30) return 0.125;
  if (activeFullMemberCount >= 20) return 0.15;
  if (activeFullMemberCount >= 15) return 0.2;
  if (activeFullMemberCount >= 5) return 0.25;
  return 1;
}

function levelFor(share: number, cap: number): ConcentrationLevel {
  if (share > cap) return "breach";
  if (share > cap * 0.8) return "watch";
  return "balanced";
}

/**
 * Network balance / capture-risk overview. The whole reason the network exists
 * is to stay balanced, so this surfaces how voting weight — including the weight
 * a firm controls through procurations — concentrates by firm, jurisdiction and
 * affiliation group, and flags anything approaching or past the holding cap.
 *
 * Read-only: computed from data the app already loads. Cooperation and
 * participation never appear here as governance currency.
 */
export function buildBalanceOverview(data: LexData, now = Date.now()): BalanceOverview {
  const participation = realParticipationPoints(data);
  const fullMembers = data.firms.filter((firm) => firm.status === "active" && firm.membershipCategory === "Full Member");
  const activeFullMemberCount = fullMembers.length;
  const firmCapShare = firmConcentrationCap(activeFullMemberCount);
  const delegationCap = delegationVoteCap(activeFullMemberCount);

  const ownWeightFor = (firmId: string) => {
    const firm = fullMembers.find((item) => item.id === firmId);
    if (!firm) return 0;
    return votingWeightForMembership({
      membershipCategory: firm.membershipCategory,
      participationBonus: calculateMembershipParticipationBonus(participation, firmId, PERIOD, firm.membershipCategory),
      decisionType: "ordinary",
      participationWeightingEnabled: true,
    });
  };

  const ownWeights = new Map<string, number>(fullMembers.map((firm) => [firm.id, ownWeightFor(firm.id)]));
  const totalWeight = [...ownWeights.values()].reduce((sum, weight) => sum + weight, 0);

  // Delegated weight a firm controls, bounded by the sliding holding cap.
  const sourceDelegations: DelegationRecord[] = data.delegations.length > 0 ? data.delegations : demoDelegations();
  const cappedDelegations = applyDelegationCaps(sourceDelegations, activeFullMemberCount, now).filter((delegation) => delegation.status === "active");
  const delegatedHeld = new Map<string, number>();
  for (const delegation of cappedDelegations) {
    if (!ownWeights.has(delegation.delegateFirmId) || !ownWeights.has(delegation.delegatingFirmId)) continue;
    delegatedHeld.set(delegation.delegateFirmId, (delegatedHeld.get(delegation.delegateFirmId) ?? 0) + (ownWeights.get(delegation.delegatingFirmId) ?? 0));
  }

  const byFirm: WeightShare[] = fullMembers
    .map((firm) => {
      const ownWeight = ownWeights.get(firm.id) ?? 0;
      const delegatedWeight = delegatedHeld.get(firm.id) ?? 0;
      const controlledWeight = ownWeight + delegatedWeight;
      const share = totalWeight > 0 ? controlledWeight / totalWeight : 0;
      return {
        id: firm.id,
        label: firm.name,
        detail: firm.jurisdiction,
        ownWeight,
        delegatedWeight,
        controlledWeight,
        share,
        level: levelFor(share, firmCapShare),
      };
    })
    .sort((a, b) => b.controlledWeight - a.controlledWeight);

  const byJurisdiction = aggregateShares(
    fullMembers,
    (firm) => ({ key: firm.jurisdiction, label: firm.jurisdiction }),
    ownWeights,
    delegatedHeld,
    totalWeight,
    JURISDICTION_THRESHOLD,
    (count) => `${count} firm${count === 1 ? "" : "s"}`,
  );

  const byAffiliation = aggregateShares(
    fullMembers.filter((firm) => firm.affiliationGroupId),
    (firm) => {
      const group = data.affiliationGroups.find((item) => item.id === firm.affiliationGroupId);
      return { key: firm.affiliationGroupId ?? "none", label: group?.name ?? "Affiliation group" };
    },
    ownWeights,
    delegatedHeld,
    totalWeight,
    firmCapShare,
    (count) => `${count} member firm${count === 1 ? "" : "s"}`,
  );

  const herfindahlIndex = byFirm.reduce((sum, item) => sum + item.share * item.share, 0);

  const alerts: BalanceAlert[] = [];
  for (const firm of byFirm) {
    if (firm.level === "breach") alerts.push({ severity: "breach", dimension: "firm", label: firm.label, message: `${firm.label} controls ${(firm.share * 100).toFixed(1)}% of voting weight, above the ${(firmCapShare * 100).toFixed(0)}% holding cap.` });
    else if (firm.level === "watch") alerts.push({ severity: "watch", dimension: "firm", label: firm.label, message: `${firm.label} is approaching the ${(firmCapShare * 100).toFixed(0)}% holding cap (${(firm.share * 100).toFixed(1)}%).` });
  }
  for (const jurisdiction of byJurisdiction) {
    if (jurisdiction.level === "breach") alerts.push({ severity: "breach", dimension: "jurisdiction", label: jurisdiction.label, message: `${jurisdiction.label} firms hold ${(jurisdiction.share * 100).toFixed(1)}% of voting weight, above the ${(JURISDICTION_THRESHOLD * 100).toFixed(0)}% jurisdiction guideline.` });
  }
  for (const affiliation of byAffiliation) {
    if (affiliation.level === "breach") alerts.push({ severity: "breach", dimension: "affiliation", label: affiliation.label, message: `${affiliation.label} controls ${(affiliation.share * 100).toFixed(1)}% of voting weight, above the ${(firmCapShare * 100).toFixed(0)}% group guideline.` });
  }

  const concentrationLabel = herfindahlIndex >= 0.25 ? "Highly concentrated" : herfindahlIndex >= 0.15 ? "Moderately concentrated" : "Well distributed";

  return {
    activeFullMemberCount,
    totalWeight,
    firmCapShare,
    jurisdictionThreshold: JURISDICTION_THRESHOLD,
    delegationCap,
    herfindahlIndex,
    concentrationLabel,
    byFirm,
    byJurisdiction,
    byAffiliation,
    alerts,
  };
}

function aggregateShares(
  firms: LexData["firms"],
  classify: (firm: LexData["firms"][number]) => { key: string; label: string },
  ownWeights: Map<string, number>,
  delegatedHeld: Map<string, number>,
  totalWeight: number,
  threshold: number,
  detail: (count: number) => string,
): WeightShare[] {
  const buckets = new Map<string, { label: string; own: number; delegated: number; count: number }>();
  for (const firm of firms) {
    const { key, label } = classify(firm);
    const bucket = buckets.get(key) ?? { label, own: 0, delegated: 0, count: 0 };
    bucket.own += ownWeights.get(firm.id) ?? 0;
    bucket.delegated += delegatedHeld.get(firm.id) ?? 0;
    bucket.count += 1;
    buckets.set(key, bucket);
  }
  return [...buckets.entries()]
    .map(([key, bucket]) => {
      const controlledWeight = bucket.own + bucket.delegated;
      const share = totalWeight > 0 ? controlledWeight / totalWeight : 0;
      return {
        id: key,
        label: bucket.label,
        detail: detail(bucket.count),
        ownWeight: bucket.own,
        delegatedWeight: bucket.delegated,
        controlledWeight,
        share,
        level: levelFor(share, threshold),
      };
    })
    .sort((a, b) => b.controlledWeight - a.controlledWeight);
}
